import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface ChatWindowState {
  open: boolean;
  minimized: boolean;
  /** the conversation shown in the window, or null for the conversation list */
  conversationId: string | null;
  /** text for the conversation list's search box, waiting for the list to take it (see openSearch) */
  search: string | null;
}

/**
 * The floating chat window's state (as MedicAI Connect's ChatUiService): one window for the whole app, mounted in the
 * dashboard shell so it stays open across page changes. Launchers (header icon, sidenav item, later the patient
 * file) only call these methods.
 */
@Injectable({ providedIn: 'root' })
export class ChatUiService {
  private readonly state = new BehaviorSubject<ChatWindowState>({ open: false, minimized: false, conversationId: null, search: null });
  readonly state$ = this.state.asObservable();

  get snapshot(): ChatWindowState {
    return this.state.value;
  }

  /** Closed -> open; minimised -> restored; open -> closed. */
  toggle(): void {
    const s = this.state.value;
    if (!s.open) this.state.next({ ...s, open: true, minimized: false });
    else if (s.minimized) this.state.next({ ...s, minimized: false });
    else this.close();
  }

  open(): void {
    this.state.next({ ...this.state.value, open: true, minimized: false });
  }

  openConversation(conversationId: string): void {
    this.state.next({ ...this.state.value, open: true, minimized: false, conversationId });
  }

  /**
   * Opens the window on the conversation list with `text` in its search box: from a patient's file, the patient's name,
   * so their own chat, referral chats and patient-app chat are all there to pick from.
   */
  openSearch(text: string): void {
    this.state.next({ open: true, minimized: false, conversationId: null, search: text });
  }

  /** The search waiting for the list, once: the list puts it in its box, and later visits keep what the user typed. */
  takeSearch(): string | null {
    const search = this.state.value.search;
    if (search !== null) this.state.next({ ...this.state.value, search: null });
    return search;
  }

  showList(): void {
    this.state.next({ ...this.state.value, conversationId: null });
  }

  setMinimized(minimized: boolean): void {
    this.state.next({ ...this.state.value, minimized });
  }

  close(): void {
    this.state.next({ ...this.state.value, open: false, minimized: false });
  }
}
